import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { getDateLocale } from '../../utils/dateLocale';
import { Send, Users, Phone, Loader2, CheckCircle, XCircle, ChevronDown, ChevronUp, AlertTriangle, Clock, Hash, Gauge, MapPin, Mail, Building } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import api from '../../services/api';
import { toast } from 'sonner';

const TARGET_OPTIONS = [
  { id: 'all', label: 'Tous les inscrits iBookSport', icon: Users, color: 'text-brand' },
  { id: 'organizers', label: 'Organisateurs', icon: Users, color: 'text-blue-600' },
  { id: 'sponsors', label: 'Sponsors', icon: Users, color: 'text-purple-600' },
  { id: 'participants', label: 'Participants', icon: Users, color: 'text-green-600' },
  { id: 'prospects', label: 'Prospects', icon: Users, color: 'text-amber-600' },
];

const AdminSmsSection = ({ smsSubscriptions, smsPurchases, smsStats, onActivate, onDeactivate, onConfirmPurchase }) => {
  const [targets, setTargets] = useState([]);
  const [eventId, setEventId] = useState('');
  const [manualPhone, setManualPhone] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [events, setEvents] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showSubscriptions, setShowSubscriptions] = useState(false);
  const [showRecipientDetail, setShowRecipientDetail] = useState(false);
  const [excludedPhones, setExcludedPhones] = useState(new Set());
  const [twilioBalance, setTwilioBalance] = useState(null);

  useEffect(() => {
    (async () => {
      try { const res = await api.get('/admin/sms/all-events'); setEvents(res.data.events || []); } catch {}
      try { const res = await api.get('/admin/sms/history'); setHistory(res.data.history || []); } catch {}
      try { const res = await api.get('/sms/twilio-balance'); setTwilioBalance(res.data); } catch {}
    })();
  }, []);

  // Preview recipients when filters change
  useEffect(() => {
    if (targets.length === 0 && !manualPhone.trim()) { setPreview(null); return; }
    const timer = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const res = await api.post('/admin/sms/recipients-preview', {
          targets, event_id: eventId, manual_phone: manualPhone.trim()
        });
        setPreview(res.data);
      } catch {
        // Fallback: count manual phone locally if API fails
        const fallback = { count: 0, breakdown: {} };
        if (manualPhone.trim()) { fallback.count += 1; fallback.breakdown.manual = 1; }
        if (targets.length > 0) { fallback.count += targets.length; fallback.breakdown.estimation = true; }
        setPreview(fallback);
      }
      setPreviewLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [targets, eventId, manualPhone]);

  const toggleTarget = (id) => {
    setExcludedPhones(new Set());
    if (id === 'all') {
      setTargets(prev => prev.includes('all') ? [] : ['all']);
    } else {
      setTargets(prev => {
        const without = prev.filter(t => t !== 'all');
        return without.includes(id) ? without.filter(t => t !== id) : [...without, id];
      });
    }
  };

  const handleSend = async () => {
    if (!message.trim()) { toast.error('Ecrivez un message'); return; }
    if (targets.length === 0 && !manualPhone.trim()) { toast.error('Selectionnez des destinataires'); return; }
    if (activeRecipientCount === 0 && !manualPhone.trim()) { toast.error('Aucun destinataire actif (tous decoches)'); return; }
    setSending(true);
    try {
      const res = await api.post('/admin/sms/send', {
        targets, event_id: eventId, manual_phone: manualPhone.trim(), message,
        exclude_phones: Array.from(excludedPhones),
      });
      toast.success(res.data.message);
      setHistory(prev => [res.data.sms, ...prev]);
      setMessage('');
      setManualPhone('');
      setExcludedPhones(new Set());
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur d\'envoi'); }
    setSending(false);
  };

  const activeRecipientCount = (preview?.count || 0) - excludedPhones.size;
  const segmentCount = message.length === 0 ? 0 : message.length <= 160 ? 1 : 2;
  const totalSms = Math.max(0, activeRecipientCount + (manualPhone.trim() ? 1 : 0)) * segmentCount;

  return (
    <div className="space-y-6" data-testid="admin-sms-section">
      {/* Twilio Balance Banner */}
      {twilioBalance?.configured && (
        <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white p-5 border" data-testid="twilio-balance-banner">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-brand/20 rounded-lg flex items-center justify-center">
                <Gauge className="w-6 h-6 text-brand" />
              </div>
              <div>
                <p className="text-xs uppercase text-slate-400 font-heading">Compte Twilio</p>
                <p className="font-heading font-black text-3xl">{twilioBalance.sms_estimate || 0} <span className="text-base font-bold text-brand">SMS dispo</span></p>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="text-[10px] uppercase text-slate-400">Solde</p>
                <p className="font-heading font-bold text-lg">{twilioBalance.balance_eur?.toFixed(2)}€</p>
              </div>
              <div className="w-px h-10 bg-slate-700" />
              <div className="text-center">
                <p className="text-[10px] uppercase text-slate-400">Cout/SMS</p>
                <p className="font-heading font-bold text-lg">{twilioBalance.cost_per_sms_eur}€</p>
              </div>
              <div className="w-px h-10 bg-slate-700" />
              <div className="text-center">
                <p className="text-[10px] uppercase text-slate-400">Admin envoyes</p>
                <p className="font-heading font-bold text-lg">{twilioBalance.total_admin_sms_sent}</p>
              </div>
              <div className="w-px h-10 bg-slate-700" />
              <div className="text-center">
                <p className="text-[10px] uppercase text-slate-400">Orga envoyes</p>
                <p className="font-heading font-bold text-lg">{twilioBalance.total_org_sms_sent}</p>
              </div>
              <div className="w-px h-10 bg-slate-700" />
              <div className="text-center">
                <p className="text-[10px] uppercase text-slate-400">Numero</p>
                <p className="font-mono text-xs text-slate-300">{twilioBalance.phone_number}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border p-4"><p className="text-xs font-heading uppercase text-slate-500">Abonnes actifs</p><p className="text-2xl font-heading font-bold text-brand">{smsStats?.active || 0}</p></div>
        <div className="bg-white border p-4"><p className="text-xs font-heading uppercase text-slate-500">En attente</p><p className="text-2xl font-heading font-bold text-orange-500">{smsStats?.pending || 0}</p></div>
        <div className="bg-white border p-4"><p className="text-xs font-heading uppercase text-slate-500">Credits vendus</p><p className="text-2xl font-heading font-bold text-slate-800">{smsStats?.total_credits_sold || 0}</p></div>
        <div className="bg-white border p-4"><p className="text-xs font-heading uppercase text-slate-500">Revenus SMS</p><p className="text-2xl font-heading font-bold text-green-600">{(smsStats?.total_revenue || 0).toFixed(2)}€</p></div>
      </div>

      {/* === COMPOSE SMS === */}
      <div className="bg-white border" data-testid="admin-sms-compose">
        <div className="p-4 border-b bg-slate-900 text-white">
          <h3 className="font-heading font-bold text-sm uppercase flex items-center gap-2">
            <Send className="w-4 h-4 text-brand" /> Envoyer un SMS Admin
          </h3>
        </div>

        <div className="p-5 space-y-5">
          {/* Target selection */}
          <div>
            <label className="text-xs font-heading uppercase text-slate-500 mb-2 block">Destinataires</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="admin-sms-targets">
              {TARGET_OPTIONS.map(opt => {
                const isSelected = targets.includes(opt.id);
                const isDisabled = opt.id !== 'all' && targets.includes('all');
                return (
                  <button
                    key={opt.id}
                    onClick={() => !isDisabled && toggleTarget(opt.id)}
                    disabled={isDisabled}
                    className={`border-2 p-3 text-left transition-all ${isSelected ? 'border-brand bg-brand/5' : isDisabled ? 'border-slate-100 bg-slate-50 opacity-50' : 'border-slate-200 hover:border-brand/50'}`}
                    data-testid={`target-${opt.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-4 h-4 border-2 rounded-sm flex items-center justify-center ${isSelected ? 'bg-brand border-brand' : 'border-slate-300'}`}>
                        {isSelected && <CheckCircle className="w-3 h-3 text-white" />}
                      </div>
                      <span className="text-xs font-heading font-bold uppercase">{opt.label}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Event filter (for participants) */}
          {targets.includes('participants') && (
            <div>
              <label className="text-xs font-heading uppercase text-slate-500 mb-1 block">Filtrer par evenement (optionnel)</label>
              <Select value={eventId} onValueChange={setEventId}>
                <SelectTrigger data-testid="admin-sms-event-select">
                  <SelectValue placeholder="Tous les participants..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_participants">Tous les participants</SelectItem>
                  {events.map(e => <SelectItem key={e.event_id} value={e.event_id}>{e.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Manual phone */}
          <div>
            <label className="text-xs font-heading uppercase text-slate-500 mb-1 block">Numero libre (optionnel)</label>
            <Input
              value={manualPhone}
              onChange={e => setManualPhone(e.target.value)}
              placeholder="+33612345678"
              className="font-mono"
              data-testid="admin-sms-manual-phone"
            />
            <p className="text-[10px] text-slate-400 mt-1">Ajoutez un numero qui n'est pas dans la base (format international +33...)</p>
          </div>

          {/* Preview count */}
          {(targets.length > 0 || manualPhone.trim()) && (
            <div className="bg-slate-50 border" data-testid="admin-sms-preview">
              <div className="p-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-slate-400" />
                  {previewLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin text-brand" />
                  ) : (
                    <span className="text-sm text-slate-700">
                      <strong className="text-brand">{activeRecipientCount}</strong>{excludedPhones.size > 0 && <span className="text-red-400">/{preview?.count || 0}</span>} destinataire(s)
                      {excludedPhones.size > 0 && (
                        <span className="text-red-400 text-xs ml-1">({excludedPhones.size} exclus)</span>
                      )}
                      {preview?.breakdown && Object.keys(preview.breakdown).length > 1 && (
                        <span className="text-slate-400 ml-2">
                          ({Object.entries(preview.breakdown).map(([k, v]) =>
                            `${k === 'organizers' ? 'Orga' : k === 'sponsors' ? 'Spons' : k === 'participants' ? 'Partic' : k === 'prospects' ? 'Prosp' : k === 'manual' ? 'Manuel' : 'Tous'}: ${v}`
                          ).join(', ')})
                        </span>
                      )}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {segmentCount > 0 && preview?.count > 0 && (
                    <span className="text-xs font-heading font-bold text-slate-600">
                      = {totalSms} segment(s) SMS
                    </span>
                  )}
                  {preview?.recipients?.length > 0 && (
                    <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-brand" onClick={() => setShowRecipientDetail(!showRecipientDetail)} data-testid="toggle-recipient-detail">
                      {showRecipientDetail ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      {showRecipientDetail ? 'Masquer' : 'Voir le detail'}
                    </Button>
                  )}
                </div>
              </div>
              {showRecipientDetail && preview?.recipients?.length > 0 && (
                <div className="border-t max-h-[350px] overflow-y-auto">
                  <div className="flex items-center justify-between px-3 py-2 bg-slate-100 sticky top-0 z-10">
                    <label className="flex items-center gap-2 cursor-pointer text-xs">
                      <input type="checkbox" className="rounded border-slate-300 accent-brand"
                        checked={excludedPhones.size === 0}
                        onChange={() => {
                          if (excludedPhones.size === 0) {
                            setExcludedPhones(new Set(preview.recipients.map(r => r.phone)));
                          } else {
                            setExcludedPhones(new Set());
                          }
                        }}
                        data-testid="select-all-recipients"
                      />
                      <span className="font-heading uppercase font-bold">Tout selectionner ({activeRecipientCount}/{preview.recipients.length})</span>
                    </label>
                    {excludedPhones.size > 0 && (
                      <button className="text-[11px] text-brand underline" onClick={() => setExcludedPhones(new Set())} data-testid="reset-exclusions">
                        Reselectionner tout
                      </button>
                    )}
                  </div>
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 sticky top-[36px] z-[5]">
                      <tr>
                        <th className="w-8 p-2"></th>
                        <th className="text-left p-2 font-heading uppercase">Nom / Organisation</th>
                        <th className="text-left p-2 font-heading uppercase">Telephone</th>
                        <th className="text-left p-2 font-heading uppercase">Email</th>
                        <th className="text-left p-2 font-heading uppercase">Ville</th>
                        <th className="text-left p-2 font-heading uppercase">Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.recipients.map((r, i) => {
                        const isExcluded = excludedPhones.has(r.phone);
                        return (
                          <tr key={i} className={`border-b border-slate-100 hover:bg-slate-50 ${isExcluded ? 'opacity-40 bg-red-50/30' : ''}`} data-testid={`recipient-row-${i}`}>
                            <td className="p-2 text-center">
                              <input type="checkbox" className="rounded border-slate-300 accent-brand cursor-pointer"
                                checked={!isExcluded}
                                onChange={() => {
                                  setExcludedPhones(prev => {
                                    const next = new Set(prev);
                                    if (isExcluded) { next.delete(r.phone); } else { next.add(r.phone); }
                                    return next;
                                  });
                                }}
                                data-testid={`checkbox-recipient-${i}`}
                              />
                            </td>
                            <td className="p-2">
                              <p className={`font-medium ${isExcluded ? 'line-through' : ''}`}>{r.organization || r.name || '-'}</p>
                              {r.organization && r.name && r.name !== r.organization && (
                                <p className="text-[10px] text-slate-400">{r.name}</p>
                              )}
                            </td>
                            <td className="p-2"><span className="flex items-center gap-1"><Phone className="w-3 h-3 text-slate-400" />{r.phone}</span></td>
                            <td className="p-2">{r.email ? <span className="flex items-center gap-1"><Mail className="w-3 h-3 text-slate-400" />{r.email}</span> : <span className="text-slate-300">-</span>}</td>
                            <td className="p-2">{r.city || '-'}</td>
                            <td className="p-2">
                              <span className={`px-1.5 py-0.5 rounded text-[10px] ${r.type === 'prospect' ? 'bg-amber-100 text-amber-700' : r.type === 'organizer' ? 'bg-blue-100 text-blue-700' : r.type === 'sponsor' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'}`}>
                                {r.type === 'prospect' ? 'Prospect' : r.type === 'organizer' ? 'Orga' : r.type === 'sponsor' ? 'Sponsor' : 'Partic'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* SMS rules */}
          <div className="bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600">
            <p className="font-bold text-slate-700 mb-1">Regles Twilio</p>
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-white border p-2 rounded">
                <p className="font-bold text-green-700">Texte classique</p>
                <p>1 SMS = <strong>160 caracteres</strong></p>
              </div>
              <div className="bg-white border p-2 rounded">
                <p className="font-bold text-orange-600">Caracteres speciaux</p>
                <p>1 SMS = <strong>70 caracteres</strong></p>
              </div>
            </div>
            <p className="mt-2 text-slate-500">Maximum 320 caracteres par message (2 segments).</p>
          </div>

          {/* Message textarea */}
          <div>
            <textarea
              className="w-full border border-slate-200 rounded p-3 text-sm resize-none focus:ring-1 focus:ring-brand focus:border-brand outline-none"
              rows={4}
              placeholder="Votre message SMS..."
              value={message}
              onChange={e => { if (e.target.value.length <= 320) setMessage(e.target.value); }}
              maxLength={320}
              data-testid="admin-sms-message"
            />
            <div className="flex items-center justify-between mt-2">
              <div className="flex items-center gap-3">
                <span className={`text-xs font-mono ${message.length > 160 ? 'text-orange-500 font-bold' : message.length > 140 ? 'text-amber-500' : 'text-slate-400'}`}>
                  {message.length}/320
                </span>
                <span className={`text-[10px] px-2 py-0.5 font-heading font-bold uppercase ${message.length === 0 ? 'bg-slate-100 text-slate-400' : message.length <= 160 ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                  {message.length === 0 ? '0 SMS' : message.length <= 160 ? '1 segment' : '2 segments'}
                </span>
              </div>
              <Button
                className="bg-brand text-white gap-2 font-heading font-bold text-xs"
                onClick={handleSend}
                disabled={sending || !message.trim() || (targets.length === 0 && !manualPhone.trim())}
                data-testid="admin-sms-send-btn"
              >
                {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                {sending ? 'Envoi en cours...' : 'Envoyer'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* === HISTORY === */}
      <div className="bg-white border">
        <button
          className="w-full p-4 border-b flex items-center justify-between hover:bg-slate-50 transition-colors"
          onClick={() => setShowHistory(!showHistory)}
          data-testid="admin-sms-history-toggle"
        >
          <h3 className="font-heading font-bold text-sm uppercase flex items-center gap-2">
            <Clock className="w-4 h-4" /> Historique des envois Admin ({history.length})
          </h3>
          {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {showHistory && (
          <div className="divide-y">
            {history.length === 0 ? (
              <p className="p-6 text-center text-sm text-slate-400">Aucun SMS admin envoye</p>
            ) : history.map(sms => (
              <div key={sms.sms_id} className="p-4" data-testid={`admin-sms-row-${sms.sms_id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 text-[10px] font-bold uppercase ${sms.status === 'sent' ? 'bg-green-100 text-green-700' : sms.status === 'queued' ? 'bg-yellow-100 text-yellow-700' : 'bg-slate-100 text-slate-700'}`}>
                      {sms.status === 'sent' ? 'Envoye' : sms.status === 'queued' ? 'File d\'attente' : sms.status}
                    </span>
                    {sms.targets && (
                      <span className="text-[10px] text-slate-400">
                        {sms.targets.map(t => t === 'all' ? 'Tous' : t === 'organizers' ? 'Orga' : t === 'sponsors' ? 'Spons' : t === 'prospects' ? 'Prosp' : 'Partic').join(', ')}
                      </span>
                    )}
                    {sms.manual_phone && <span className="text-[10px] text-slate-400 flex items-center gap-1"><Phone className="w-2.5 h-2.5" />{sms.manual_phone}</span>}
                  </div>
                  <span className="text-[10px] text-slate-400">
                    {sms.created_at && format(new Date(sms.created_at), 'd MMM yyyy HH:mm', { locale: getDateLocale() })}
                  </span>
                </div>
                <p className="text-sm text-slate-700 bg-slate-50 p-2 rounded">{sms.message}</p>
                <div className="flex items-center gap-4 mt-2">
                  <span className="text-xs text-slate-400"><Users className="w-3 h-3 inline mr-1" />{sms.recipient_count} dest.</span>
                  {sms.sent_count > 0 && <span className="text-xs text-green-600"><CheckCircle className="w-3 h-3 inline mr-1" />{sms.sent_count} envoye(s)</span>}
                  {sms.failed_count > 0 && <span className="text-xs text-red-500"><XCircle className="w-3 h-3 inline mr-1" />{sms.failed_count} echec(s)</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* === SUBSCRIPTIONS MANAGEMENT === */}
      <div className="bg-white border">
        <button
          className="w-full p-4 border-b flex items-center justify-between hover:bg-slate-50 transition-colors"
          onClick={() => setShowSubscriptions(!showSubscriptions)}
          data-testid="admin-sms-subs-toggle"
        >
          <h3 className="font-heading font-bold text-sm uppercase flex items-center gap-2">
            <Hash className="w-4 h-4" /> Gestion abonnements organisateurs ({smsSubscriptions?.length || 0})
          </h3>
          {showSubscriptions ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {showSubscriptions && (
          <>
            {/* Pending purchases */}
            {smsPurchases?.filter(p => p.status === 'pending').length > 0 && (
              <div className="border-b">
                <div className="p-4 bg-amber-50"><p className="font-heading font-bold text-xs uppercase text-amber-800">Achats en attente de confirmation</p></div>
                <div className="divide-y">
                  {smsPurchases.filter(p => p.status === 'pending').map(p => (
                    <div key={p.purchase_id} className="p-4 flex items-center justify-between">
                      <div>
                        <p className="font-heading font-bold text-sm">{p.user_name || p.user_id}</p>
                        <p className="text-xs text-slate-500">{p.pack_name} — <strong>{p.price}€</strong> — {p.payment_method}</p>
                      </div>
                      <Button size="sm" className="bg-green-600 text-white text-xs" onClick={() => onConfirmPurchase(p.purchase_id)}>Confirmer</Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Subscriptions list */}
            <div className="divide-y">
              {(!smsSubscriptions || smsSubscriptions.length === 0) ? (
                <p className="p-6 text-center text-sm text-slate-400">Aucun organisateur n'a demande le service SMS</p>
              ) : smsSubscriptions.map(sub => (
                <div key={sub.subscription_id || sub.user_id} className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-2.5 h-2.5 rounded-full ${sub.status === 'active' ? 'bg-green-500' : sub.status === 'pending' ? 'bg-amber-500' : 'bg-red-500'}`} />
                    <div>
                      <p className="font-heading font-bold text-sm">{sub.user_name}</p>
                      <p className="text-xs text-slate-500">{sub.user_email}</p>
                    </div>
                    <div className="ml-4 text-xs text-slate-600">
                      <span className="font-bold">{sub.credits}</span> credits | <span>{sub.total_sent || 0}</span> envoyes | <span>{(sub.total_spent || 0).toFixed(2)}€</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {sub.status === 'pending' && <Button size="sm" className="bg-brand text-white text-xs" onClick={() => onActivate(sub.user_id, 0)}>Activer</Button>}
                    {sub.status === 'active' && <Button size="sm" variant="outline" className="text-xs text-red-600 border-red-200" onClick={() => onDeactivate(sub.user_id)}>Suspendre</Button>}
                    {sub.status === 'suspended' && <Button size="sm" className="bg-brand text-white text-xs" onClick={() => onActivate(sub.user_id, sub.credits)}>Reactiver</Button>}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminSmsSection;
